import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Activity } from "lucide-react";
import { fmtDateGB, ukShort } from "../../lib/format";
import { api } from "../../lib/api";

const VERDICT = {
  healthy: { label: "Meter reads true", c: "#15934F" },
  watch: { label: "Minor drift — watch", c: "#B45309" },
  investigate: { label: "Significant drift — investigate", c: "#C0392B" },
  no_data: { label: "No overlapping data", c: "#64748B" },
};

export default function GenerationComparison({ projectId }) {
  const [data, setData] = useState(null);
  const [drift, setDrift] = useState([]);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    api.get(`/monitoring/${projectId}/gen-comparison?days=${days}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [projectId, days]);

  useEffect(() => {
    let alive = true;
    api.get(`/monitoring/${projectId}/drift-history?limit=20`)
      .then(({ data }) => { if (alive) setDrift(data.events || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [projectId]);

  // Render when the site has both sources now, or has past drift events to show.
  if (!data || (!data.has_both && drift.length === 0)) return null;
  const v = VERDICT[data.verdict] || VERDICT.no_data;

  return (
    <section className="swiss-card p-5 mb-6" data-testid="gen-comparison">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div>
          <div className="label flex items-center gap-2"><Activity size={12} /> Meter vs inverter generation</div>
          <div className="font-display text-lg font-semibold mt-1">Generation meter cross-check</div>
        </div>
        <div className="flex items-center gap-3">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input-swiss py-1 text-xs w-auto" data-testid="gen-comparison-days">
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
          {data.has_both && <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: v.c + "22", color: v.c }} data-testid="gen-comparison-verdict">{v.label}</span>}
        </div>
      </div>

      {data.has_both && (
        <>
          <div className="flex flex-wrap gap-5 my-3 text-sm">
            <div><span className="muted text-xs">Meter total</span><div className="font-mono font-bold text-[#2563EB]">{data.total_meter_kwh.toLocaleString()} kWh</div></div>
            <div><span className="muted text-xs">Inverter total</span><div className="font-mono font-bold text-[#16A39B]">{data.total_inverter_kwh.toLocaleString()} kWh</div></div>
            <div><span className="muted text-xs">Overall variance</span><div className="font-mono font-bold" style={{ color: v.c }}>{data.overall_variance_pct != null ? `${data.overall_variance_pct > 0 ? "+" : ""}${data.overall_variance_pct}%` : "—"}</div></div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={data.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={ukShort} />
                <YAxis tick={{ fontSize: 10 }} unit=" kWh" width={64} />
                <Tooltip labelFormatter={fmtDateGB} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="meter_generation_kwh" name="Meter" stroke="#2563EB" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="inverter_generation_kwh" name="Inverter" stroke="#16A39B" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] muted mt-2">
            Generation is reported primarily by the generation meter. When an inverter is also connected, its cloud reading is
            plotted here as an independent cross-check — a large, sustained gap suggests a meter fault or a CT/wiring issue.
          </p>
        </>
      )}

      {drift.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--line)]" data-testid="drift-history">
          <div className="label mb-2">Past drift events · {drift.length}</div>
          <div className="space-y-1.5">
            {drift.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-[12px] py-1 border-b border-[var(--line-soft,#EEF2F4)]" data-testid={`drift-event-${e.id}`}>
                <span className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: e.severity === "critical" ? "#C0392B" : "#B45309" }} />
                  <span className="font-mono text-xs">{fmtDateGB(e.detected_on)}</span>
                  <span className="muted">meter {e.avg_variance_pct > 0 ? "+" : ""}{e.avg_variance_pct}% vs inverter over {e.days}d (±{e.threshold_pct}%)</span>
                </span>
                <span className="text-[10px] font-bold" style={{ color: e.severity === "critical" ? "#C0392B" : "#B45309" }}>{e.severity}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
